import * as React from 'react'
import { Box } from '@material-ui/core'
import { BackendPaginationMeta, PaginationMeta } from '../../types/notification'

export type TagSize = 'xs' | 'sm' | 'md' | 'lg'

const TAG_SIZE_STYLES: Record<TagSize, { fontSize: number; borderRadius: number; padding: string }> = {
  xs: { fontSize: 10, borderRadius: 10, padding: '1px 6px' },
  sm: { fontSize: 11, borderRadius: 12, padding: '2px 8px' },
  md: { fontSize: 12, borderRadius: 12, padding: '2px 10px' },
  lg: { fontSize: 12, borderRadius: 12, padding: '4px 10px' },
}

interface ColorTagProps {
  color: string
  label: React.ReactNode
  size?: TagSize
  style?: React.CSSProperties
}

export const ColorTag: React.FC<ColorTagProps> = ({ color, label, size, style }) => {
  const sz = TAG_SIZE_STYLES[size || 'sm']
  return (
    <Box
      component="span"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        fontSize: sz.fontSize,
        fontWeight: 600,
        color: color,
        backgroundColor: color + '14',
        border: '1px solid ' + color,
        borderRadius: sz.borderRadius,
        padding: sz.padding,
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        ...style,
      }}
    >
      {label}
    </Box>
  )
}

// Meta lookups — single source of truth for labels + colours.
export interface PriorityMeta { label: string; color: string }
const PRIORITY_META_MAP: Record<string, PriorityMeta> = {
  low:    { label: '低',   color: '#444444' },
  normal: { label: '通常', color: '#1976d2' },
  high:   { label: '高',   color: '#f57c00' },
  urgent: { label: '緊急', color: '#d32f2f' },
}
const PRIORITY_FALLBACK: PriorityMeta = { label: '', color: '#bdbdbd' }
export function priorityMeta(p: string): PriorityMeta {
  const m = PRIORITY_META_MAP[p]
  return m ? m : { label: p, color: PRIORITY_FALLBACK.color }
}
export const PRIORITY_ORDER: ReadonlyArray<'low' | 'normal' | 'high' | 'urgent'> =
  ['low', 'normal', 'high', 'urgent']

const STATUS_LABELS: Record<string, string> = {
  draft: '下書き',
  scheduled: '予約済',
  sent: '送信済',
  canceled: 'キャンセル',
  expired: '期限切れ',
}
const STATUS_COLOR_MAP: Record<string, string> = {
  draft:    '#9e9e9e',
  scheduled:'#1976d2',
  sent:     '#388e3c',
  canceled: '#d32f2f',
  expired:  '#f57c00',
}
export function statusLabel(s: string): string {
  return STATUS_LABELS[s] || s
}
export function statusColor(s: string): string {
  return STATUS_COLOR_MAP[s] || '#9e9e9e'
}
export const STATUS_ORDER: ReadonlyArray<'draft' | 'scheduled' | 'sent' | 'canceled' | 'expired'> =
  ['draft', 'scheduled', 'sent', 'canceled', 'expired']

export interface TypeMeta { label: string; color: string }
const TYPE_META_MAP: Record<string, TypeMeta> = {
  manual:   { label: '手動',         color: '#1976d2' },
  auto:     { label: '自動',         color: '#388e3c' },
  maintain: { label: 'メンテナンス', color: '#6a1b9a' },
}
export function typeMeta(t: string): TypeMeta {
  const m = TYPE_META_MAP[t]
  return m ? m : { label: t, color: '#9e9e9e' }
}
export const TYPE_ORDER: ReadonlyArray<'manual' | 'maintain'> =
  ['manual', 'maintain']

interface MetaTagProps {
  size?: TagSize
  style?: React.CSSProperties
}

export const PriorityTag: React.FC<MetaTagProps & { priority: string }> = (props) => {
  const m = priorityMeta(props.priority)
  return <ColorTag color={m.color} label={m.label} size={props.size} style={props.style} />
}

export const TypeTag: React.FC<MetaTagProps & { type: string }> = (props) => {
  const m = typeMeta(props.type)
  return <ColorTag color={m.color} label={m.label} size={props.size} style={props.style} />
}

export const StatusTag: React.FC<MetaTagProps & { status: string }> = (props) => {
  return (
    <ColorTag
      color={statusColor(props.status)}
      label={statusLabel(props.status)}
      size={props.size}
      style={props.style}
    />
  )
}

interface CreatorBadgeProps {
  creator: { id: number; name?: string | null } | null | undefined
  currentUserId: number | null | undefined
  size?: TagSize
  style?: React.CSSProperties
}
export const CreatorBadge: React.FC<CreatorBadgeProps> = ({ creator, currentUserId, size, style }) => {
  const isMine = !!(creator && currentUserId != null && creator.id === currentUserId)
  const name = (creator && creator.name) || '—'
  return <ColorTag color={isMine ? '#388e3c' : '#444444'} label={name} size={size} style={style} />
}

export function formatJpDateTime(dateStr: string | null | undefined, fallback: string = ''): string {
  if (!dateStr) return fallback
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return fallback || dateStr
    return d.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (e) {
    return fallback || dateStr || ''
  }
}

export function errorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in (e as object)) {
    const m = (e as { message?: unknown }).message
    if (typeof m === 'string' && m) return m
  }
  return fallback
}

// Adapt the backend's shared Paginatable wire shape ({ page, total, ... }) to the
// store-internal PaginationMeta ({ current_page, total_count, ... }) so the rest
// of the notification UI keeps using the same field names.
export function mapPaginationMeta(p: BackendPaginationMeta): PaginationMeta {
  return {
    current_page: p.page,
    total_pages: p.total_pages,
    total_count: p.total,
  }
}
